import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "@/lib/cart";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";

import Home from "@/pages/Home";
import Universe from "@/pages/Universe";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Confirmation from "@/pages/Confirmation";
import Account from "@/pages/Account";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Magazine from "@/pages/Magazine";
import Recipes from "@/pages/Recipes";
import Catering from "@/pages/Catering";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/Products";
import AdminOrders from "@/pages/admin/Orders";
import AdminClients from "@/pages/admin/Clients";
import AdminStock from "@/pages/admin/Stock";
import AdminPromotions from "@/pages/admin/Promotions";
import AdminDelivery from "@/pages/admin/Delivery";
import AdminContent from "@/pages/admin/Content";
import AdminCMS from "@/pages/admin/CMS";
import AdminManagers from "@/pages/admin/Managers";
import AdminNewsletter from "@/pages/admin/Newsletter";
import AdminTestimonialsMod from "@/pages/admin/Testimonials";
import AdminSides from "@/pages/admin/Sides";
import AdminAccount from "@/pages/admin/Account";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import AdminTeam from "@/pages/admin/Team";
import AdminEventQuotes from "@/pages/admin/EventQuotes";
import AdminEventRealizations from "@/pages/admin/EventRealizations";
import Events from "@/pages/Events";
import ScrollToTop from "@/components/ScrollToTop";

const RequireStaff = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--dsf-text-secondary)]">Loading…</div>;
  if (!user || (user.role !== "admin" && user.role !== "manager")) return <Navigate to="/admin/login" replace />;
  return children;
};

const RequireAdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;
  return children;
};

const RequirePerm = ({ perm, children }) => {
  const { user, loading, isAdmin, hasPerm } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin && !hasPerm(perm)) return <Navigate to="/admin" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Toaster position="top-center" richColors />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/univers/:slug" element={<Universe />} />
                <Route path="/produit/:slug" element={<Product />} />
                <Route path="/panier" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/confirmation/:orderId" element={<Confirmation />} />
                <Route path="/compte" element={<Account />} />

                <Route path="/connexion" element={<Login />} />
                <Route path="/inscription" element={<Register />} />
                <Route path="/verifier-email" element={<VerifyEmail />} />

                <Route path="/a-propos" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/magazine" element={<Magazine />} />
                <Route path="/recettes" element={<Recipes />} />
                <Route path="/traiteur" element={<Catering />} />
                <Route path="/evenements" element={<Events />} />
                <Route path="/events" element={<Events />} />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <RequireStaff>
                      <AdminLayout />
                    </RequireStaff>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route path="produits" element={<AdminProducts />} />
                  <Route path="commandes" element={<AdminOrders />} />
                  <Route path="clients" element={<AdminClients />} />
                  <Route path="stock" element={<AdminStock />} />
                  <Route path="promotions" element={<AdminPromotions />} />
                  <Route path="livraison" element={<AdminDelivery />} />
                  <Route path="contenu" element={<AdminContent />} />
                  <Route path="cms" element={<AdminCMS />} />
                  <Route path="team" element={<RequirePerm perm="cms.edit"><AdminTeam /></RequirePerm>} />
                  <Route path="events/devis" element={<RequirePerm perm="events.manage"><AdminEventQuotes /></RequirePerm>} />
                  <Route path="events/realisations" element={<RequirePerm perm="events.manage"><AdminEventRealizations /></RequirePerm>} />
                  <Route path="managers" element={<RequireAdminOnly><AdminManagers /></RequireAdminOnly>} />
                  <Route path="newsletter" element={<RequireAdminOnly><AdminNewsletter /></RequireAdminOnly>} />
                  <Route path="avis" element={<RequireAdminOnly><AdminTestimonialsMod /></RequireAdminOnly>} />
                  <Route path="sides" element={<RequirePerm perm="products.manage"><AdminSides /></RequirePerm>} />
                  <Route path="compte" element={<AdminAccount />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
